import { Fragment } from 'react';
import { useBoolean } from 'usehooks-ts';

import { DocumentActionItem } from '@/components/Form/ActionList/DocumentActionItem';

import { useDrawerOpenTracker } from '@/context';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import {
  selectBasicInfoStatus,
  selectUserData,
} from '@/redux/user-data/selectors';

import {
  DocumentSection,
  DocumentStatus,
  RequiredDocumentSection,
} from '@/types/document';
import { User } from '@/types/user';

import { isExistingCustomer } from '@/utils/user';

import { RequiredDocumentSelectDrawer } from '../drawers/RequiredDocumentSelectDrawer';

export function USCitizenshipRenouncement() {
  const { t } = useTranslate();
  const { user, requiredDocumentSections, documentByDocType } =
    useAppSelector(selectUserData);

  const {
    value: isDrawerOpen,
    setTrue: openDrawer,
    setFalse: closeDrawer,
  } = useBoolean(false);

  useDrawerOpenTracker(isDrawerOpen);

  const basicInfoStatus = useAppSelector(selectBasicInfoStatus);
  const isBasicInfoProcessed =
    basicInfoStatus === DocumentStatus.InReview ||
    basicInfoStatus === DocumentStatus.Approved ||
    basicInfoStatus === DocumentStatus.Rejected;

  const usCitizenshipRenouncementSection = requiredDocumentSections.find(
    section => section.type === DocumentSection.USCitizenshipRenouncement,
  );

  if (!usCitizenshipRenouncementSection) return null;

  const checkBtnDisableForEXC = (
    section: RequiredDocumentSection,
    isNotPendingAndNoDocs: boolean,
  ) => {
    if (!(user as User).clientReviewed) {
      return true;
    } else {
      if (section.status === DocumentStatus.Approved) {
        return true;
      }
      /**
       * If the EXC has already signed/uploaded section docs then set it true,
       * otherwise set it false
       */
      return isNotPendingAndNoDocs;
    }
  };

  const isButtonDisabled = (section: RequiredDocumentSection) => {
    if (!user) return true;
    const isNotPendingAndNoDocs =
      section.status !== DocumentStatus.Pending && !section.docs.length;
    if (user && isExistingCustomer(user)) {
      return checkBtnDisableForEXC(section, isNotPendingAndNoDocs);
    }

    return isNotPendingAndNoDocs;
  };

  return (
    <Fragment key={DocumentSection.USCitizenshipRenouncement}>
      <DocumentActionItem
        title={t(`documentSection.US_Citizenship_Renouncement.title`)}
        description={t('status.pending')}
        onClick={openDrawer}
        status={usCitizenshipRenouncementSection.status}
        enabledForAllStatuses
        disabled={
          (user && isExistingCustomer(user) && !user.clientReviewed) ||
          !isBasicInfoProcessed
        }
        disabledButton={isButtonDisabled(usCitizenshipRenouncementSection)}
      />
      <RequiredDocumentSelectDrawer
        open={isDrawerOpen}
        section={usCitizenshipRenouncementSection}
        onClose={closeDrawer}
        documentByDocType={documentByDocType}
      />
    </Fragment>
  );
}
